import React, { useEffect, useState } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useOwnSupplierProfile } from '../../hooks/useSupplierProfiles';
import {
  COUNTRIES,
  SHIPPING_COST_UNITS,
  SHIPPING_REGIONS,
  SupplierProfileFormState,
  VAT_TREATMENTS,
  buildProfileInput,
  formatSyncInterval,
  toFormState,
} from '../../lib/supplierProfile';
import SupplierProfileCard from '../SupplierProfileCard';

interface Props {
  providerId: string;
}

const labelCls = 'block text-sm font-medium text-white/70 mb-1.5';
const inputCls = 'w-full px-3 py-2 text-sm text-white';

/**
 * Where a supplier states its own terms. Merchants see the result, read-only, on the
 * supplier card and before they request a connection (SupplierProfileCard).
 */
const SupplierProfileForm: React.FC<Props> = ({ providerId }) => {
  const { profile, canEdit, loaded, saving, error, save } = useOwnSupplierProfile(providerId);
  const [form, setForm] = useState<SupplierProfileFormState>(() => toFormState(null));
  const [errors, setErrors] = useState<string[]>([]);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (loaded) setForm(toFormState(profile));
  }, [loaded, profile]);

  const set = (patch: Partial<SupplierProfileFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setSavedAt(null);
  };

  const setShipping = (region: string, field: 'min' | 'max', value: string) => {
    setForm((prev) => ({
      ...prev,
      shipping: { ...prev.shipping, [region]: { ...prev.shipping[region], [field]: value } },
    }));
    setSavedAt(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { input, errors: problems } = buildProfileInput(form, profile?.shipping_times);
    setErrors(problems);
    if (!input) return;
    if (await save(input)) setSavedAt(Date.now());
  };

  const sync = formatSyncInterval(profile?.sync_interval_minutes);
  const included = form.shipping_cost_unit === 'included';
  const disabled = !canEdit || saving;

  return (
    <div className="glass-card rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-white">Supplier profile</h3>
      <p className="text-sm text-white/60 mt-1 mb-5">
        Merchants see these terms before they connect to you. Accurate shipping times mean fewer
        surprised customers and fewer questions for you.
      </p>

      {!loaded ? (
        <p className="text-white/30 text-sm">Loading…</p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          {!canEdit && (
            <div className="bg-white/[0.04] border border-white/10 rounded-xl p-3 text-sm text-white/60">
              Only an owner or admin of this supplier account can change the profile.
            </div>
          )}

          {/* Shipping times */}
          <div>
            <span className={labelCls}>Shipping time, in working days</span>
            <p className="text-xs text-white/40 mb-3">
              From order to delivery, including the time you need to get the pair if you buy on
              demand. Leave a region blank if you do not ship there.
            </p>
            <div className="space-y-2">
              {SHIPPING_REGIONS.map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-28 text-sm text-white/70">{label}</span>
                  <input
                    type="number" min={0} max={90} inputMode="numeric"
                    aria-label={`${label}: fastest, in days`}
                    placeholder="from"
                    value={form.shipping[key]?.min ?? ''}
                    onChange={(e) => setShipping(key, 'min', e.target.value)}
                    disabled={disabled}
                    className="w-24 px-3 py-2 text-sm text-white"
                  />
                  <span className="text-white/30 text-sm">to</span>
                  <input
                    type="number" min={0} max={90} inputMode="numeric"
                    aria-label={`${label}: slowest, in days`}
                    placeholder="to"
                    value={form.shipping[key]?.max ?? ''}
                    onChange={(e) => setShipping(key, 'max', e.target.value)}
                    disabled={disabled}
                    className="w-24 px-3 py-2 text-sm text-white"
                  />
                  <span className="text-white/30 text-sm">days</span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="sp-country" className={labelCls}>Country shipped from</label>
              <select
                id="sp-country"
                value={form.ships_from_country}
                onChange={(e) => set({ ships_from_country: e.target.value })}
                disabled={disabled}
                className={inputCls}
              >
                <option value="">Not stated</option>
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="sp-vat" className={labelCls}>VAT treatment</label>
              <select
                id="sp-vat"
                value={form.vat_treatment}
                onChange={(e) => set({ vat_treatment: e.target.value as SupplierProfileFormState['vat_treatment'] })}
                disabled={disabled}
                className={inputCls}
              >
                <option value="">Not stated</option>
                {VAT_TREATMENTS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
              {form.vat_treatment && (
                <p className="text-xs text-white/40 mt-1">
                  {VAT_TREATMENTS.find((v) => v.value === form.vat_treatment)?.hint}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="sp-cost" className={labelCls}>Shipping / label cost</label>
              <div className="flex gap-2">
                <input
                  id="sp-cost"
                  type="text" inputMode="decimal"
                  placeholder={included ? '—' : 'e.g. 8'}
                  value={included ? '' : form.shipping_cost_amount}
                  onChange={(e) => set({ shipping_cost_amount: e.target.value })}
                  disabled={disabled || included}
                  className="w-24 px-3 py-2 text-sm text-white"
                />
                <span className="self-center text-sm text-white/40">{form.shipping_cost_currency}</span>
                <select
                  aria-label="Shipping cost is charged"
                  value={form.shipping_cost_unit}
                  onChange={(e) => set({ shipping_cost_unit: e.target.value as SupplierProfileFormState['shipping_cost_unit'] })}
                  disabled={disabled}
                  className="flex-1 px-3 py-2 text-sm text-white"
                >
                  <option value="">Choose…</option>
                  {SHIPPING_COST_UNITS.map((u) => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="sp-max" className={labelCls}>Maximum catalogue size</label>
              <input
                id="sp-max"
                type="number" min={1} inputMode="numeric"
                placeholder="Number of products"
                value={form.max_catalog_size}
                onChange={(e) => set({ max_catalog_size: e.target.value })}
                disabled={disabled}
                className={inputCls}
              />
              <p className="text-xs text-white/40 mt-1">The most products you list at one time.</p>
            </div>
          </div>

          <div>
            <label htmlFor="sp-notes" className={labelCls}>Anything else merchants should know</label>
            <textarea
              id="sp-notes"
              rows={3} maxLength={1000}
              placeholder="e.g. We buy on demand, so pairs ship 4–5 days after the order. No returns on worn pairs."
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              disabled={disabled}
              className={inputCls}
            />
          </div>

          <div className="text-sm text-white/50">
            Stock refresh: <span className="text-white">{sync ?? 'set by how often you send updates'}</span>
            <span className="text-white/30"> — managed by FlowStock, shown to merchants as is.</span>
          </div>

          {(errors.length > 0 || error) && (
            <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400 space-y-1">
              {errors.map((msg) => <p key={msg}>{msg}</p>)}
              {error && <p>{error}</p>}
            </div>
          )}

          {canEdit && (
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-5 py-2 rounded-full bg-orange-500 hover:bg-orange-400 text-black text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save profile'}
              </button>
              {savedAt && (
                <span className="inline-flex items-center text-sm text-emerald-400">
                  <CheckCircleIcon className="w-4 h-4 mr-1" /> Saved. Merchants see this now.
                </span>
              )}
            </div>
          )}

          <div>
            <p className="text-xs text-white/40 mb-2">What merchants see (last saved version)</p>
            <SupplierProfileCard profile={profile} />
          </div>
        </form>
      )}
    </div>
  );
};

export default SupplierProfileForm;
